import { useEffect, useState } from 'react'
import { Link, Navigate } from 'react-router-dom'
import * as settingsApi from '@/api/settings'
import { useAuth } from '@/auth/AuthContext'
import { SettingsShell } from '@/components/SettingsShell'
import { getRememberedSettingsCustomer } from '@/settingsContext'
import { useSiteSettings } from '@/siteSettingsContext'

export function ExpirySettings() {
  const { isAdmin } = useAuth()
  const { applySettings, refresh } = useSiteSettings()
  const [customerId] = useState(() => getRememberedSettingsCustomer())
  const [warningDays, setWarningDays] = useState(5)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [saved, setSaved] = useState(false)

  useEffect(() => {
    if (!isAdmin) return undefined
    let cancelled = false
    async function load() {
      setLoading(true)
      setError('')
      try {
        const data = await settingsApi.getSiteSettings()
        if (!cancelled) {
          setWarningDays(Number(data.expiry_warning_days) || 5)
          applySettings(data)
        }
      } catch (err) {
        if (!cancelled) {
          setError(
            typeof err.response?.data?.detail === 'string'
              ? err.response.data.detail
              : 'Could not load expiry settings.',
          )
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    load()
    return () => {
      cancelled = true
    }
  }, [isAdmin, applySettings])

  if (!isAdmin) {
    return <Navigate to="/" replace />
  }

  async function handleSubmit(e) {
    e.preventDefault()
    const days = Number(warningDays)
    if (!Number.isInteger(days) || days < 1 || days > 365) {
      setError('Enter a whole number of days between 1 and 365.')
      return
    }
    setSaving(true)
    setError('')
    setSaved(false)
    try {
      const updated = await settingsApi.updateSiteSettings({
        expiry_warning_days: days,
      })
      applySettings(updated)
      await refresh()
      setWarningDays(Number(updated.expiry_warning_days) || days)
      setSaved(true)
    } catch (err) {
      setError(
        typeof err.response?.data?.detail === 'string'
          ? err.response.data.detail
          : 'Could not save expiry settings.',
      )
    } finally {
      setSaving(false)
    }
  }

  return (
    <SettingsShell title="Expiry warning" customerId={customerId || undefined}>
      <div className="space-y-6">
        <div className="rounded-xl border border-slate-200 bg-white/90 px-8 py-8 shadow-sm">
          <h1 className="font-display text-4xl tracking-tight text-brand">
            Expiry warning
          </h1>
          <p className="mt-2 max-w-2xl text-sm text-brand-muted">
            Applies to <strong>Expiry</strong> fields only (certificates, etc.).
            Rows due within this many days (and already overdue) appear on{' '}
            <Link to="/expiries" className="underline">
              ToDo / Expiring
            </Link>
            . Open <strong>ToDo</strong> due dates always appear there, regardless
            of this window.
          </p>
        </div>

        {error && (
          <p className="text-sm text-red-700" role="alert">
            {error}
          </p>
        )}
        {saved && (
          <p className="rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-900">
            Expiry warning window saved.
          </p>
        )}

        {loading ? (
          <p className="text-brand-muted">Loading…</p>
        ) : (
          <form
            onSubmit={handleSubmit}
            className="space-y-4 rounded-xl border border-slate-200 bg-white/90 p-6 shadow-sm"
          >
            <label className="block max-w-xs text-sm">
              <span className="text-brand-muted">Warn this many days ahead</span>
              <input
                type="number"
                min={1}
                max={365}
                step={1}
                value={warningDays}
                onChange={(e) => setWarningDays(e.target.value)}
                className="mt-1 w-full rounded border border-slate-300 px-3 py-2"
                required
              />
              <span className="mt-1 block text-xs text-brand-muted">
                Default is 5. Already overdue items are always included.
              </span>
            </label>
            <button
              type="submit"
              disabled={saving}
              className="rounded-md bg-brand px-4 py-2 text-sm font-semibold text-white hover:bg-slate-800 disabled:opacity-50"
            >
              {saving ? 'Saving…' : 'Save'}
            </button>
          </form>
        )}
      </div>
    </SettingsShell>
  )
}
